import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ChatBubbleBottomCenterIcon,
  Cog6ToothIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  StarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export default function BottlenecksHelp() {
  const [activePopup, setActivePopup] = useState(null);

  const showPopup = (popupId) => {
    setActivePopup(popupId);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  const Popup = ({ title, description, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative">
          <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-t-2xl px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{title}</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Bottlenecks Management</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive bottleneck identification and resolution system for managers to
            address workflow issues and provide targeted assistance to team members.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Bottlenecks Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-200">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Issue Detection</h3>
                <p className="text-red-700 text-sm leading-relaxed">
                  Automatically identifies tasks where staff members are experiencing difficulties
                  and require managerial assistance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleBottomCenterIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Solution Collaboration</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Enables managers and team members to collaboratively develop and share
                  solutions for complex task issues.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <Cog6ToothIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Assistance</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Leverages artificial intelligence to generate intelligent suggestions
                  and automated solutions for common workflow bottlenecks.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Resolution Tracking</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Monitors the progress of issue resolution and ensures bottlenecks
                  are effectively addressed and closed out.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Demo */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interactive Bottlenecks Demo</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Sample Bottleneck Task Card */}
            <div className="bg-red-500 rounded-xl p-6 text-white mb-8">
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-4">
                    <h3 className="text-xl font-bold">Customer Service Call</h3>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">John Smith</span>
                  </div>

                  <p className="text-red-100 mb-4">Task Description: Handle incoming customer inquiries and resolve issues</p>
                  <p className="text-red-100 mb-4">Substep: Initial Customer Greeting</p>
                  <p className="text-red-100 mb-6">Issue: Difficulty understanding customer accent and communication barriers</p>

                  {/* Rating System Demo */}
                  <div className="bg-white/10 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold mb-2">Rate Manager Response:</h4>
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => showPopup('rating-system')}
                          className="cursor-pointer transition-colors hover:text-yellow-300"
                        >
                          <StarIcon className="h-6 w-6 text-yellow-400" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Help Response Demo */}
                  <div className="bg-emerald-500/20 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold mb-2">Manager Suggestion:</h4>
                    <p className="text-sm mb-2">"Try using our translation guide and speak slowly. If needed, transfer to a supervisor."</p>
                    <p className="text-xs text-emerald-200">Manager Name • 2 hours ago</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => showPopup('suggest-solution')}
                      className="cursor-pointer transition-colors hover:bg-white/20 bg-white/10 px-4 py-2 rounded-lg flex items-center space-x-2"
                    >
                      <ChatBubbleBottomCenterIcon className="h-5 w-5" />
                      <span>Suggest Solution</span>
                    </button>

                    <button
                      onClick={() => showPopup('mark-complete')}
                      className="cursor-pointer transition-colors hover:bg-white/20 bg-white/10 px-4 py-2 rounded-lg flex items-center space-x-2"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                      <span>Mark as Complete</span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => showPopup('ai-suggestion')}
                    className="cursor-pointer transition-colors hover:bg-indigo-700 bg-indigo-600 p-2 rounded-lg"
                    title="Generate AI suggestion"
                  >
                    <Cog6ToothIcon className="h-6 w-6 text-white" />
                  </button>

                  <button
                    onClick={() => showPopup('send-suggestion')}
                    className="cursor-pointer transition-colors hover:bg-emerald-700 bg-emerald-600 p-2 rounded-lg"
                    title="Send suggestion"
                  >
                    <PaperAirplaneIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-red-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-red-900">Rating System</h4>
                </div>
                <p className="text-red-700 text-sm leading-relaxed">
                  Staff members can rate manager responses on a 5-star scale to provide
                  feedback on the helpfulness and quality of suggestions received.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">AI Assistance</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Generate intelligent suggestions using artificial intelligence to help
                  resolve complex workflow issues and bottlenecks.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Solution Submission</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Submit your suggestions directly to staff members experiencing issues,
                  enabling real-time collaborative problem-solving.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Issue Resolution</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Mark issues as complete once they have been successfully resolved,
                  tracking the effectiveness of bottleneck management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Collaborative Help</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  View and respond to help requests from team members, fostering
                  a supportive environment for issue resolution.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Real-time Updates</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  System automatically updates every minute to show new bottlenecks
                  and track resolution progress in real-time.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Bottlenecks Management Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Issue Response Guidelines</h3>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Respond to bottleneck alerts within 15 minutes</li>
                    <li>• Assess issue severity before providing solutions</li>
                    <li>• Use AI suggestions as starting points, not final answers</li>
                    <li>• Follow up on implemented solutions</li>
                    <li>• Document successful resolution strategies</li>
                  </ul>
                </div>

                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                  <h3 className="text-lg font-bold text-emerald-900 mb-3">Communication Tips</h3>
                  <ul className="space-y-2 text-sm text-emerald-700">
                    <li>• Be clear and concise in your suggestions</li>
                    <li>• Explain the reasoning behind your recommendations</li>
                    <li>• Encourage staff to ask follow-up questions</li>
                    <li>• Use positive, supportive language</li>
                    <li>• Acknowledge when issues are complex</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Prevention Strategies</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Monitor task completion patterns regularly</li>
                    <li>• Identify recurring issues and address root causes</li>
                    <li>• Provide additional training for problematic tasks</li>
                    <li>• Adjust staffing levels during peak periods</li>
                    <li>• Update procedures based on successful resolutions</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Quality Assurance</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Review staff ratings of your responses</li>
                    <li>• Track resolution success rates</li>
                    <li>• Identify patterns in bottleneck causes</li>
                    <li>• Share best practices across the team</li>
                    <li>• Continuously improve response quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Components */}
      {activePopup === 'rating-system' && (
        <Popup
          title="Rating System"
          description="Click on stars (1-5) to rate the helpfulness of manager responses. This feedback helps improve the quality of future suggestions and tracks response effectiveness."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'ai-suggestion' && (
        <Popup
          title="AI Suggestion Generator"
          description="Click this button to generate intelligent suggestions using artificial intelligence. The AI analyzes the issue description and provides automated solution recommendations."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'send-suggestion' && (
        <Popup
          title="Send Suggestion"
          description="Click this button to submit your written suggestion to the staff member experiencing the bottleneck. The suggestion will be sent immediately and tracked for resolution."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'suggest-solution' && (
        <Popup
          title="Suggest Solution"
          description="Click this button to open a text input field where you can write and send suggestions to help staff members resolve their workflow bottlenecks."
          isOpen={true}
          onClose={closePopup}
        />
      )}
      {activePopup === 'mark-complete' && (
        <Popup
          title="Mark as Complete"
          description="Click this button when a bottleneck issue has been successfully resolved. This removes the issue from the active bottlenecks list and tracks resolution metrics."
          isOpen={true}
          onClose={closePopup}
        />
      )}
    </div>
  );
}